import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const TransferReview = ({ recipient, quote, complianceData, onBack }) => {
  const navigate = useNavigate();
  const [finalConfirmation, setFinalConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmitTransfer = async () => {
    if (!finalConfirmation) return;

    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      
      // Redirect to transaction history after 3 seconds
      setTimeout(() => {
        navigate('/transaction-history');
      }, 3000);
    }, 2000);
  };

  const getSourceOfFundsLabel = (value) => {
    const options = {
      'salary': 'Salary/Employment Income',
      'business': 'Business Income',
      'savings': 'Personal Savings',
      'investment': 'Investment Returns',
      'gift': 'Gift from Family/Friends',
      'loan': 'Loan Proceeds',
      'inheritance': 'Inheritance',
      'other': 'Other'
    };
    return options?.[value] || value;
  };

  const getTransferPurposeLabel = (value) => {
    const options = {
      'family_support': 'Family Support/Maintenance',
      'education': 'Education Expenses',
      'medical': 'Medical Expenses',
      'business': 'Business Transaction',
      'property': 'Property Purchase/Investment',
      'gift': 'Gift',
      'loan_repayment': 'Loan Repayment',
      'travel': 'Travel Expenses',
      'other': 'Other'
    };
    return options?.[value] || value;
  };

  const getRelationshipLabel = (value) => {
    const options = {
      'family': 'Family Member',
      'friend': 'Friend',
      'business_partner': 'Business Partner',
      'employee': 'Employee',
      'service_provider': 'Service Provider',
      'myself': 'Myself (own account)',
      'other': 'Other'
    };
    return options?.[value] || value;
  };

  if (showSuccess) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" size={40} className="text-success" />
        </div>
        <h2 className="text-3xl font-semibold text-foreground mb-4">Transfer Submitted Successfully!</h2>
        <p className="text-lg text-muted-foreground mb-6">
          Your transfer has been submitted and is being processed. You'll receive updates via email and SMS.
        </p>
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center justify-center space-x-4 mb-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Transaction ID</p>
              <p className="font-mono font-medium text-primary">TXN-2025-{Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="text-center">
              <p className="text-muted-foreground">Amount Sent</p>
              <p className="font-medium">${quote?.sendAmount} USD</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground">Amount Received</p>
              <p className="font-medium">{quote?.receiveAmount} {recipient?.currency}</p>
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Redirecting to transaction history in a few seconds...
        </p>
        <Button onClick={() => navigate('/transaction-history')} variant="outline">
          View Transaction History
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Review & Confirm</h2>
          <p className="text-muted-foreground mt-1">
            Please review all details before submitting your transfer
          </p>
        </div>
        <Button variant="ghost" onClick={onBack} iconName="ArrowLeft" iconPosition="left">
          Back
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Transfer Details */}
        <div className="space-y-6">
          {/* Recipient Information */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="User" size={20} className="mr-2" />
              Recipient Details
            </h3>
            <div className="flex items-start space-x-4">
              <Image
                src={recipient?.avatar}
                alt={recipient?.avatarAlt}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div className="flex-1">
                <h4 className="font-medium text-foreground">{recipient?.name}</h4>
                <p className="text-sm text-muted-foreground">{recipient?.email}</p>
                <div className="mt-2 space-y-1">
                  <p className="text-sm">
                    <span className="text-muted-foreground">Country:</span> {recipient?.country}
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Bank:</span> {recipient?.bankName}
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Account:</span> {recipient?.accountNumber}
                  </p>
                </div>
                <div className="flex items-center space-x-1 mt-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm text-success">Verified Recipient</span>
                </div>
              </div>
            </div>
          </div>

          {/* Transfer Amount */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="DollarSign" size={20} className="mr-2" />
              Transfer Amount
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">You Send:</span>
                <span className="text-xl font-semibold text-foreground">${quote?.sendAmount} USD</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Exchange Rate:</span>
                <span className="font-medium">1 USD = {quote?.exchangeRate} {recipient?.currency}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Total Fees:</span>
                <span className="font-medium">${quote?.fees?.total?.toFixed(2)} USD</span>
              </div>
              <div className="border-t border-border pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">They Receive:</span>
                  <span className="text-xl font-semibold text-primary">{quote?.receiveAmount} {recipient?.currency}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Transfer Type & Timeline */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Clock" size={20} className="mr-2" />
              Transfer Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Transfer Type:</span>
                <span className="font-medium capitalize">{quote?.transferType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Processing Time:</span>
                <span className="font-medium">
                  {quote?.transferType === 'standard' && '1-3 business days'}
                  {quote?.transferType === 'express' && 'Same day'}
                  {quote?.transferType === 'instant' && 'Within minutes'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Transfer Date:</span>
                <span className="font-medium">{new Date()?.toLocaleDateString('en-GB')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Information */}
        <div className="space-y-6">
          {/* Compliance Details */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Shield" size={20} className="mr-2" />
              Compliance Information
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-muted-foreground">Source of Funds:</span>
                <p className="font-medium">{getSourceOfFundsLabel(complianceData?.sourceOfFunds)}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Transfer Purpose:</span>
                <p className="font-medium">{getTransferPurposeLabel(complianceData?.transferPurpose)}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Relationship:</span>
                <p className="font-medium">{getRelationshipLabel(complianceData?.relationship)}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Your Occupation:</span>
                <p className="font-medium">{complianceData?.occupation}</p>
              </div>
              {complianceData?.additionalInfo && (
                <div>
                  <span className="text-sm text-muted-foreground">Additional Information:</span>
                  <p className="font-medium text-sm">{complianceData?.additionalInfo}</p>
                </div>
              )}
            </div>
          </div>

          {/* Security & Compliance Checks */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Lock" size={20} className="mr-2" />
              Security Checks
            </h3>
            <div className="space-y-3">
              {[
                { label: 'Identity Verification', status: 'completed' },
                { label: 'AML Screening', status: 'completed' },
                { label: 'Sanctions Check', status: 'completed' },
                { label: 'Risk Assessment', status: 'completed' },
                { label: 'Compliance Review', status: 'completed' }
              ]?.map((check, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{check?.label}</span>
                  <div className="flex items-center space-x-1">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-sm text-success">Passed</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Important Information */}
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-6">
            <h3 className="font-medium text-foreground mb-3 flex items-center">
              <Icon name="AlertTriangle" size={20} className="mr-2 text-warning" />
              Important Information
            </h3>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>• Once submitted, this transfer cannot be cancelled</li>
              <li>• Exchange rates are locked for this transaction</li>
              <li>• You will receive email and SMS updates</li>
              <li>• Funds will be debited from your account immediately</li>
              <li>• Processing times may vary based on recipient bank</li>
            </ul>
          </div>

          {/* Final Confirmation */}
          <div className="bg-card border border-border rounded-lg p-6">
            <Checkbox
              label="I confirm that all information is accurate and I authorize this transfer"
              description="By checking this box, you agree to proceed with the transfer and accept our terms and conditions."
              checked={finalConfirmation}
              onChange={(e) => setFinalConfirmation(e?.target?.checked)}
              required
            />
          </div>
        </div>
      </div>
      {/* Submit Button */}
      <div className="flex justify-end pt-6 border-t border-border">
        <Button
          onClick={handleSubmitTransfer}
          disabled={!finalConfirmation}
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
          className="min-w-48"
        >
          {isSubmitting ? 'Processing Transfer...' : 'Submit Transfer'}
        </Button>
      </div>
    </div>
  );
};

export default TransferReview;